import {PivotCellChartData} from 'entities/chartData';

function roundPivotValue(value: number): number {
    return Math.round(value * 100) / 100;
}

function getCellRowKey(cell: PivotCellChartData & {row_key?: string}): string {
    return cell.rowKey ?? cell.row_key ?? '';
}

function getCellColKey(cell: PivotCellChartData & {col_key?: string}): string {
    return cell.colKey ?? cell.col_key ?? '';
}

export interface PivotMatrixRow {
    rowKey: string;
    values: Record<string, number | string>;
    rowTotal: number;
}

export interface PivotMatrixData {
    columns: string[];
    rows: PivotMatrixRow[];
    columnTotals: Record<string, number>;
    grandTotal: number;
}

export function pivotCellsToMatrix(cells: PivotCellChartData[]): PivotMatrixData {
    const rowKeys = [...new Set(cells.map(getCellRowKey).filter(Boolean))];
    const columnKeys = [...new Set(cells.map(getCellColKey).filter(Boolean))];

    const valueMap = new Map<string, number>();
    for (const cell of cells) {
        const rowKey = getCellRowKey(cell);
        const colKey = getCellColKey(cell);
        if (!rowKey || !colKey) {
            continue;
        }
        valueMap.set(`${rowKey}\u0000${colKey}`, cell.value);
    }

    const columnTotals: Record<string, number> = {};
    const rows: PivotMatrixRow[] = rowKeys.map((rowKey) => {
        const values: Record<string, number | string> = {};
        let rowTotal = 0;

        for (const columnKey of columnKeys) {
            const value = valueMap.get(`${rowKey}\u0000${columnKey}`) ?? '';
            values[columnKey] = value;
            if (typeof value === 'number') {
                rowTotal += value;
                columnTotals[columnKey] = (columnTotals[columnKey] ?? 0) + value;
            }
        }

        return {rowKey, values, rowTotal: roundPivotValue(rowTotal)};
    });

    rows.sort((a, b) => b.rowTotal - a.rowTotal);
    const sortedColumns = [...columnKeys].sort(
        (a, b) => (columnTotals[b] ?? 0) - (columnTotals[a] ?? 0),
    );

    const roundedColumnTotals = Object.fromEntries(
        Object.entries(columnTotals).map(([key, value]) => [key, roundPivotValue(value)]),
    );
    const grandTotal = roundPivotValue(rows.reduce((sum, row) => sum + row.rowTotal, 0));

    return {
        columns: sortedColumns,
        rows,
        columnTotals: roundedColumnTotals,
        grandTotal,
    };
}
